import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  cn,
} from "@safeplate/ui";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock3,
  MessageSquareText,
  PackageCheck,
  RefreshCw,
  ShieldCheck,
  X,
} from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { getRecord, recordSummary } from "../records/records-api.js";
import { RecordDrawer } from "../records/records-page.js";
import { useShell } from "../shell/shell-context.js";
import {
  createDisposition,
  listFollowUps,
  resolveFollowUp,
  respondToFollowUp,
  severityTone,
  triggerLabel,
  type FollowUp,
  type FollowUpDecision,
  type FollowUpSeverity,
  type FollowUpStatus,
  type FoodDisposition,
} from "./followups-api.js";

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-NZ", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function FollowUpDrawer({
  canManage,
  followUp,
  onChanged,
  onClose,
}: {
  canManage: boolean;
  followUp: FollowUp;
  onChanged: (followUp: FollowUp) => void;
  onClose: () => void;
}) {
  const [showRecord, setShowRecord] = useState(false);
  const [showDisposition, setShowDisposition] = useState(false);
  const [dispositions, setDispositions] = useState<FoodDisposition[]>([]);
  const record = useQuery({
    enabled: Boolean(followUp.record_id),
    queryFn: () => getRecord(followUp.record_id!),
    queryKey: ["record", followUp.record_id],
  });
  const respond = useMutation({
    mutationFn: (response: string) => respondToFollowUp(followUp.id, response),
    onSuccess: onChanged,
  });
  const resolve = useMutation({
    mutationFn: (form: FormData) =>
      resolveFollowUp(followUp.id, {
        decision: String(form.get("decision")) as FollowUpDecision,
        note: String(form.get("note") ?? "").trim() || null,
      }),
    onSuccess: onChanged,
  });
  const disposition = useMutation({
    mutationFn: (form: FormData) =>
      createDisposition(followUp.id, {
        batch_lot: String(form.get("batch_lot") ?? "").trim() || null,
        decision: String(form.get("decision")) as FoodDisposition["decision"],
        note: String(form.get("note") ?? "").trim() || null,
        product: String(form.get("product") ?? "").trim(),
        quantity: String(form.get("quantity") ?? "").trim() || null,
      }),
    onSuccess: (created) => {
      setDispositions((current) => [...current, created]);
      setShowDisposition(false);
    },
  });
  const overdue =
    followUp.due_at &&
    new Date(followUp.due_at).getTime() < Date.now() &&
    followUp.status !== "closed";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-stone-950/45 p-0 backdrop-blur-sm sm:p-5">
      <section
        aria-label="Follow-up details"
        aria-modal="true"
        className="ml-auto min-h-dvh w-full max-w-3xl bg-surface shadow-2xl sm:min-h-0 sm:rounded-3xl"
        role="dialog"
      >
        <header className="sticky top-0 z-10 flex items-start justify-between border-b border-stone-200 bg-surface/95 px-5 py-5 backdrop-blur sm:rounded-t-3xl sm:px-7">
          <div>
            <div className="flex flex-wrap gap-2">
              <Badge tone={severityTone(followUp.severity)}>
                {followUp.severity}
              </Badge>
              <Badge>{followUp.status.replaceAll("_", " ")}</Badge>
              {overdue ? <Badge tone="danger">Overdue</Badge> : null}
            </div>
            <h2 className="mt-3 text-xl font-black">
              {triggerLabel(followUp.trigger_kind)}
            </h2>
            <p className="mt-1 text-sm text-stone-500">
              Raised {formatDate(followUp.created_at)}
            </p>
          </div>
          <Button
            aria-label="Close follow-up"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </header>
        <div className="space-y-6 p-5 sm:p-7">
          {followUp.status === "closed" ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 text-emerald-900">
                <CheckCircle2 className="size-4" />
                <p className="text-sm font-black">
                  Closed as {followUp.manager_decision?.replaceAll("_", " ")}
                </p>
              </div>
              {followUp.manager_note ? (
                <p className="mt-2 text-sm leading-6 text-emerald-900/75">
                  {followUp.manager_note}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-800" />
                <div>
                  <p className="text-sm font-black text-amber-950">
                    Kitchen work continued
                  </p>
                  <p className="mt-1 text-sm leading-6 text-amber-900/75">
                    This item needs review, but it never blocked the person
                    completing their shift.
                  </p>
                </div>
              </div>
            </div>
          )}

          <section>
            <h3 className="text-sm font-black">What needs attention</h3>
            <p className="mt-2 rounded-xl border border-stone-200 bg-surface p-4 text-sm leading-6 text-stone-700">
              {followUp.suggested_action ??
                "Review the related evidence and record the practical decision taken."}
            </p>
            {followUp.due_at ? (
              <p
                className={cn(
                  "mt-2 flex items-center gap-2 text-xs font-semibold",
                  overdue ? "text-red-700" : "text-stone-500",
                )}
              >
                <Clock3 className="size-3.5" /> Due{" "}
                {formatDate(followUp.due_at)}
              </p>
            ) : null}
          </section>

          {followUp.record_id ? (
            <section>
              <h3 className="text-sm font-black">Related evidence</h3>
              <button
                className="mt-2 flex w-full items-center gap-3 rounded-xl border border-stone-200 bg-surface p-4 text-left transition hover:border-emerald-700"
                onClick={() => setShowRecord(true)}
                type="button"
              >
                <span className="grid size-9 place-items-center rounded-lg bg-stone-100 text-stone-600">
                  <ShieldCheck className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold">
                    {record.data
                      ? record.data.record_type_code.replaceAll("_", " ")
                      : "Loading record"}
                  </p>
                  <p className="mt-1 truncate text-xs text-stone-500">
                    {record.data
                      ? recordSummary(record.data)
                      : "Open the submitted values and rule result"}
                  </p>
                </div>
                <ChevronRight className="size-4 text-stone-300" />
              </button>
            </section>
          ) : null}

          {followUp.staff_response ? (
            <section>
              <h3 className="text-sm font-black">Staff response</h3>
              <div className="mt-2 rounded-xl border border-stone-200 bg-surface p-4">
                <p className="text-sm leading-6 text-stone-700">
                  {followUp.staff_response}
                </p>
                {followUp.staff_responded_at ? (
                  <p className="mt-2 text-xs text-stone-400">
                    Responded {formatDate(followUp.staff_responded_at)}
                  </p>
                ) : null}
              </div>
            </section>
          ) : null}

          {dispositions.length > 0 ? (
            <section>
              <h3 className="text-sm font-black">
                Food disposition recorded now
              </h3>
              <div className="mt-2 space-y-2">
                {dispositions.map((item) => (
                  <div
                    className="flex items-start gap-3 rounded-xl border border-stone-200 bg-surface p-4"
                    key={item.id}
                  >
                    <PackageCheck className="mt-0.5 size-4 text-emerald-700" />
                    <div>
                      <p className="text-sm font-bold">
                        {item.product} · {item.decision}
                      </p>
                      <p className="mt-1 text-xs text-stone-500">
                        {[item.batch_lot, item.quantity, item.note]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {!canManage && followUp.status !== "closed" ? (
            <form
              className="rounded-2xl border border-stone-200 bg-surface p-4"
              onSubmit={(event: FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                respond.mutate(
                  String(
                    new FormData(event.currentTarget).get("response") ?? "",
                  ),
                );
              }}
            >
              <div className="flex items-center gap-2">
                <MessageSquareText className="size-4 text-stone-400" />
                <h3 className="text-sm font-black">
                  Tell your manager what you did
                </h3>
              </div>
              <textarea
                className="mt-3 min-h-24 w-full rounded-xl border border-stone-300 p-3 text-sm"
                name="response"
                placeholder="Moved the food to another fridge and rechecked it…"
                required
              />
              {respond.error ? (
                <p className="mt-2 text-sm font-semibold text-red-800">
                  {displayError(respond.error)}
                </p>
              ) : null}
              <div className="mt-3 text-right">
                <Button disabled={respond.isPending} type="submit">
                  {respond.isPending ? "Sending…" : "Send response"}
                </Button>
              </div>
            </form>
          ) : null}

          {canManage && followUp.status !== "closed" ? (
            <section className="rounded-2xl border border-stone-200 bg-surface p-4 sm:p-5">
              <h3 className="text-sm font-black">Manager decision</h3>
              <p className="mt-1 text-xs leading-5 text-stone-500">
                Correct the related record first when the submitted evidence is
                wrong; then close this item with the matching decision.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {followUp.record_id ? (
                  <Button onClick={() => setShowRecord(true)} variant="outline">
                    Open evidence <ArrowRight className="size-4" />
                  </Button>
                ) : null}
                <Button
                  onClick={() => setShowDisposition((current) => !current)}
                  variant="outline"
                >
                  <PackageCheck className="size-4" /> Record food disposition
                </Button>
              </div>
              {showDisposition ? (
                <form
                  className="mt-4 grid gap-3 rounded-xl bg-stone-50 p-4 sm:grid-cols-2"
                  onSubmit={(event: FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    disposition.mutate(new FormData(event.currentTarget));
                  }}
                >
                  <div className="sm:col-span-2">
                    <Label htmlFor="disposition-product">Food or product</Label>
                    <Input
                      className="mt-1"
                      id="disposition-product"
                      name="product"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="disposition-batch">Batch or lot</Label>
                    <Input
                      className="mt-1"
                      id="disposition-batch"
                      name="batch_lot"
                    />
                  </div>
                  <div>
                    <Label htmlFor="disposition-quantity">Quantity</Label>
                    <Input
                      className="mt-1"
                      id="disposition-quantity"
                      name="quantity"
                    />
                  </div>
                  <div>
                    <Label htmlFor="disposition-decision">Decision</Label>
                    <select
                      className="mt-1 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
                      id="disposition-decision"
                      name="decision"
                    >
                      <option value="quarantine">Quarantine</option>
                      <option value="discard">Discard</option>
                      <option value="rework">Rework</option>
                      <option value="release">Release</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="disposition-note">Note</Label>
                    <Input className="mt-1" id="disposition-note" name="note" />
                  </div>
                  {disposition.error ? (
                    <p className="text-sm font-semibold text-red-800 sm:col-span-2">
                      {displayError(disposition.error)}
                    </p>
                  ) : null}
                  <div className="text-right sm:col-span-2">
                    <Button
                      disabled={disposition.isPending}
                      size="sm"
                      type="submit"
                    >
                      {disposition.isPending ? "Saving…" : "Save disposition"}
                    </Button>
                  </div>
                </form>
              ) : null}
              <form
                className="mt-5 border-t border-stone-200 pt-5"
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  resolve.mutate(new FormData(event.currentTarget));
                }}
              >
                <div>
                  <Label htmlFor="manager-decision">Close as</Label>
                  <select
                    className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
                    id="manager-decision"
                    name="decision"
                  >
                    <option value="resolved">Resolved</option>
                    <option value="edited_record">Record corrected</option>
                    <option value="accepted_variance">Accepted variance</option>
                    <option value="escalated">Escalated</option>
                    <option value="dismissed">Dismissed</option>
                  </select>
                </div>
                <div className="mt-3">
                  <Label htmlFor="manager-note">Decision note</Label>
                  <textarea
                    className="mt-1.5 min-h-20 w-full rounded-xl border border-stone-300 p-3 text-sm"
                    id="manager-note"
                    name="note"
                  />
                </div>
                {resolve.error ? (
                  <p className="mt-2 text-sm font-semibold text-red-800">
                    {displayError(resolve.error)}
                  </p>
                ) : null}
                <div className="mt-4 text-right">
                  <Button disabled={resolve.isPending} type="submit">
                    {resolve.isPending ? "Closing…" : "Close follow-up"}{" "}
                    <CheckCircle2 className="size-4" />
                  </Button>
                </div>
              </form>
            </section>
          ) : null}
        </div>
      </section>
      {showRecord && followUp.record_id ? (
        <RecordDrawer
          canManage={canManage}
          onClose={() => setShowRecord(false)}
          recordId={followUp.record_id}
        />
      ) : null}
    </div>
  );
}

export function AttentionWorkspacePage() {
  const shell = useShell();
  const queryClient = useQueryClient();
  const locationId = shell.activeLocation?.id;
  const canManage = shell.activeRole !== "staff";
  const [status, setStatus] = useState<FollowUpStatus | "">("open");
  const [severity, setSeverity] = useState<FollowUpSeverity | "">("");
  const [selected, setSelected] = useState<FollowUp | null>(null);
  const followUps = useInfiniteQuery({
    enabled: Boolean(locationId),
    getNextPageParam: (page: Awaited<ReturnType<typeof listFollowUps>>) =>
      page.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listFollowUps(
        locationId!,
        { ...(severity ? { severity } : {}), ...(status ? { status } : {}) },
        pageParam,
      ),
    queryKey: ["follow-ups", locationId, status, severity],
  });
  const items = useMemo(
    () =>
      followUps.data?.pages.flatMap(
        (page: Awaited<ReturnType<typeof listFollowUps>>) => page.items,
      ) ?? [],
    [followUps.data],
  );

  return (
    <>
      <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.68rem] font-bold tracking-[0.14em] text-emerald-800 uppercase">
            Review
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-[-0.045em] text-stone-950 sm:text-4xl">
            Needs attention
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
            Review exceptions after the kitchen has kept moving, capture what
            happened to food, and close the loop.
          </p>
        </div>
        <Button
          disabled={followUps.isFetching}
          onClick={() => void followUps.refetch()}
          variant="outline"
        >
          <RefreshCw
            className={cn("size-4", followUps.isFetching && "animate-spin")}
          />{" "}
          Refresh
        </Button>
      </header>
      {!locationId ? (
        <EmptyState
          description="Choose a location to see its follow-ups."
          title="Choose a location"
        />
      ) : (
        <div className="space-y-4">
          <Card>
            <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2">
                <AlertTriangle className="size-4 text-amber-700" />
                <p className="text-sm font-black">Attention queue</p>
              </div>
              <div className="flex flex-1 flex-wrap gap-2 sm:justify-end">
                <select
                  aria-label="Follow-up status"
                  className="h-10 rounded-xl border border-stone-300 bg-surface px-3 text-sm"
                  onChange={(event) =>
                    setStatus(event.target.value as FollowUpStatus | "")
                  }
                  value={status}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In progress</option>
                  <option value="closed">Closed</option>
                  <option value="">All statuses</option>
                </select>
                <select
                  aria-label="Follow-up severity"
                  className="h-10 rounded-xl border border-stone-300 bg-surface px-3 text-sm"
                  onChange={(event) =>
                    setSeverity(event.target.value as FollowUpSeverity | "")
                  }
                  value={severity}
                >
                  <option value="">All severities</option>
                  <option value="critical">Critical</option>
                  <option value="major">Major</option>
                  <option value="minor">Minor</option>
                </select>
              </div>
            </CardContent>
          </Card>
          {followUps.isPending ? (
            <LoadingState label="Loading follow-ups" />
          ) : null}
          {followUps.error ? (
            <ErrorState
              message={displayError(followUps.error)}
              onAction={() => void followUps.refetch()}
              title="Follow-ups aren’t available"
            />
          ) : null}
          {!followUps.isPending && !followUps.error && items.length === 0 ? (
            <EmptyState
              calm
              description={
                status === "open"
                  ? "There are no open food-safety follow-ups for this location."
                  : "No follow-ups match these filters."
              }
              title={
                status === "open"
                  ? "Nothing needs attention"
                  : "No matching follow-ups"
              }
            />
          ) : null}
          {items.length > 0 ? (
            <Card className="overflow-hidden">
              <div className="divide-y divide-stone-100">
                {items.map((item) => {
                  const overdue =
                    item.due_at &&
                    new Date(item.due_at).getTime() < Date.now() &&
                    item.status !== "closed";
                  return (
                    <button
                      className="flex w-full items-center gap-3 px-4 py-4 text-left transition hover:bg-stone-50 sm:px-5"
                      key={item.id}
                      onClick={() => setSelected(item)}
                      type="button"
                    >
                      <AlertTriangle
                        className={cn(
                          "size-4 shrink-0",
                          item.severity === "critical"
                            ? "text-red-800"
                            : item.severity === "major"
                              ? "text-amber-800"
                              : "text-stone-600",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-black">
                            {triggerLabel(item.trigger_kind)}
                          </p>
                          <Badge tone={severityTone(item.severity)}>
                            {item.severity}
                          </Badge>
                          {item.staff_response ? (
                            <Badge>Staff responded</Badge>
                          ) : null}
                          {overdue ? (
                            <Badge tone="danger">Overdue</Badge>
                          ) : null}
                        </div>
                        <p className="mt-1 truncate text-xs text-stone-500">
                          {item.suggested_action ?? "Manager review requested"}{" "}
                          · {formatDate(item.created_at)}
                        </p>
                      </div>
                      <ChevronRight className="size-4 shrink-0 text-stone-300" />
                    </button>
                  );
                })}
              </div>
            </Card>
          ) : null}
          {followUps.hasNextPage ? (
            <div className="text-center">
              <Button
                disabled={followUps.isFetchingNextPage}
                onClick={() => void followUps.fetchNextPage()}
                variant="outline"
              >
                {followUps.isFetchingNextPage
                  ? "Loading…"
                  : "Load older follow-ups"}
              </Button>
            </div>
          ) : null}
        </div>
      )}
      {selected ? (
        <FollowUpDrawer
          canManage={canManage}
          followUp={selected}
          onChanged={async (updated) => {
            setSelected(updated);
            await queryClient.invalidateQueries({ queryKey: ["follow-ups"] });
          }}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </>
  );
}
